import React from 'react'
import { Box, Chip, Tooltip, Typography, alpha } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import type { TypographyProps } from '@mui/material'
import { getExceptionTypeMeta } from '../SystemIcons'

/**
 * Shared, consistent rendering of an exception's business classification
 * (Process Issue / Data Discrepancy) as a subtle inline lead-in. The icon +
 * label come from the single source `EXCEPTION_TYPE_META` (SystemIcons), so
 * every surface - inspector list cards, the exception report header, platform
 * reports - stays identical: the type glyph + label in the type's accent
 * colour (teal for data, purple for process), no background fill.
 *
 * Pass `description` to render the type as an inline lead-in followed by a
 * muted `·` separator and the description, flowing as normal wrapping text
 * ("Process Issue · Open but fully paid"). With no `exceptionType` it renders
 * the description alone; with neither it renders nothing. `iconSize` is 14 for
 * lists / 16 for page headers; `descriptionVariant` matches the host's body
 * style.
 */
const TYPE_TOOLTIPS: Record<string, string> = {
  process_issue: 'Process Issue - a workflow, compliance or process gap to fix',
  data_discrepancy: 'Data Discrepancy - a mismatch between systems or values',
}

export interface ExceptionTypeLabelProps {
  exceptionType?: string | null
  /** Optional description rendered after a muted `·` separator (inline only). */
  description?: string | null
  /** Icon size in px - 14 for lists, 16 for page headers. */
  iconSize?: number
  /** Typography variant for the description text (defaults to caption). */
  descriptionVariant?: TypographyProps['variant']
  /** `inline` (default) = icon + label as flowing text; `pill` = soft-filled
   * chip (icon + label, tinted background) for badge contexts; `icon` = the
   * tinted type glyph alone (tooltip + titleAccess carry the full label) for a
   * list's title gutter - no label text, no description. */
  variant?: 'inline' | 'pill' | 'icon'
  sx?: SxProps<Theme>
}

const ExceptionTypeLabel: React.FC<ExceptionTypeLabelProps> = ({
  exceptionType,
  description,
  iconSize = 14,
  descriptionVariant = 'caption',
  variant = 'inline',
  sx,
}) => {
  const meta = exceptionType ? getExceptionTypeMeta(exceptionType) : null

  // Soft-filled badge: icon + label, tinted background in the type's accent.
  if (variant === 'pill') {
    if (!meta) return null
    const Glyph = meta.icon
    return (
      <Tooltip title={TYPE_TOOLTIPS[exceptionType as string] ?? meta.label}>
        <Chip
          icon={Glyph ? <Glyph sx={{ fontSize: iconSize }} /> : undefined}
          label={meta.label}
          size="small"
          sx={{
            height: 22,
            // Root padding wins over MUI's .MuiChip-iconSmall left margin, so
            // the glyph gets real breathing room from the pill edge.
            pl: '6px',
            fontSize: '0.7rem',
            fontWeight: 600,
            color: meta.color,
            backgroundColor: alpha(meta.color, 0.12),
            '& .MuiChip-icon': { color: 'inherit', fontSize: iconSize },
            ...sx,
          }}
        />
      </Tooltip>
    )
  }

  if (!meta && !description) return null

  const Icon = meta?.icon

  // Icon-only: the tinted type glyph alone, for a list's title gutter. Tooltip
  // (hover) + `titleAccess` (the SVG <title> + img role for screen readers)
  // both carry the full label. The two glyphs are shape-distinct (Difference
  // vs AltRoute) so the signal survives in greyscale / colour-blindness.
  if (variant === 'icon') {
    if (!meta || !Icon) return null
    return (
      <Tooltip title={TYPE_TOOLTIPS[exceptionType as string] ?? meta.label}>
        <Icon
          titleAccess={meta.label}
          sx={{ fontSize: iconSize, color: meta.color }}
        />
      </Tooltip>
    )
  }

  return (
    <Box sx={sx}>
      {meta && (
        <Tooltip title={TYPE_TOOLTIPS[exceptionType as string] ?? meta.label}>
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              verticalAlign: 'middle',
              // Muted label text - the icon carries the type colour signal so
              // the colour isn't repeated as noise on every row.
              color: 'text.secondary',
            }}
          >
            {Icon && <Icon sx={{ fontSize: iconSize, color: meta.color }} />}
            <Typography
              component="span"
              sx={{
                fontSize: iconSize >= 16 ? '0.875rem' : '0.75rem',
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              {meta.label}
            </Typography>
          </Box>
        </Tooltip>
      )}
      {meta && description && (
        <Box
          component="span"
          sx={{ color: '#333', opacity: 0.5, mx: 0.75, verticalAlign: 'middle' }}
        >
          ·
        </Box>
      )}
      {description && (
        <Typography
          component="span"
          variant={descriptionVariant}
          color="text.secondary"
          sx={{ verticalAlign: 'middle' }}
        >
          {description}
        </Typography>
      )}
    </Box>
  )
}

export default ExceptionTypeLabel
